/**
 * Utility for organizing raw execution steps into a hierarchical timeline structure.
 *
 * This module handles the complex task of parsing employee execution data and organizing it into:
 * - Top-level employee phases (fetch-context, planner, validator, executor)
 * - Nested tool executions under their parent phases
 * - User-created workflows at the top level
 *
 * @module organize-timeline
 */

/**
 * Base fields common to all execution steps
 */
interface BaseExecutionStep {
  version: string;
  executionId: string;
  tenantId: string;
  status: string;
  createdAt?: string;
}

/**
 * Employee phase execution step (e.g., planner, executor, validator, fetch-context)
 */
export interface EmployeePhaseStep extends BaseExecutionStep {
  phase: string;
  stepNumber: number;
  employeeId: string;
  stepId?: string;
  outputs?: Record<string, unknown>;
}

/**
 * Tool execution step (called by employee phases)
 */
export interface ToolExecutionStep extends BaseExecutionStep {
  toolName: string;
  workflowId: string;
  stepId: string;
  outputs?: Record<string, unknown>;
  parentExecution?: {
    stepId: string;
    id: string;
  };
}

/**
 * Workflow execution (user-created workflows)
 */
export interface WorkflowExecutionStep extends BaseExecutionStep {
  workflowId: string;
  inputs?: Record<string, unknown>;
  outputs?: Record<string, unknown>;
  executionDepth?: number;
  parentExecution?: {
    stepId: string;
    id: string;
  };
}

/**
 * Union type for all possible raw execution step formats
 */
export type RawExecutionStep =
  | EmployeePhaseStep
  | ToolExecutionStep
  | WorkflowExecutionStep;

/**
 * Organized timeline step with type discrimination
 */
export interface TimelineStep {
  type: 'employee-phase' | 'tool' | 'workflow';
  data: RawExecutionStep;
  children: TimelineStep[];
}

/**
 * Parsed parent execution information from stepId
 */
interface ParsedParentExecution {
  phase: string;
  stepNumber: number;
  iteration: number;
  executionId: string;
}

/**
 * Type guard to check if step is an employee phase
 */
function isEmployeePhase(step: RawExecutionStep): step is EmployeePhaseStep {
  return 'phase' in step && !('workflowId' in step);
}

/**
 * Type guard to check if step is a tool execution
 */
function isToolExecution(step: RawExecutionStep): step is ToolExecutionStep {
  return (
    'toolName' in step &&
    'workflowId' in step &&
    (step as ToolExecutionStep).workflowId.startsWith('employee-tools-')
  );
}

/**
 * Type guard to check if step is a workflow execution container (to be filtered out)
 */
function isWorkflowContainer(step: RawExecutionStep): boolean {
  return (
    'workflowId' in step &&
    !('toolName' in step) &&
    (step as WorkflowExecutionStep).workflowId.startsWith('employee-tools-')
  );
}

/**
 * Type guard to check if step should be filtered out
 */
function shouldFilterStep(step: RawExecutionStep): boolean {
  // Filter out internal event publications
  if (
    'stepId' in step &&
    step.stepId === 'publish-employee-step-completed-event'
  ) {
    return true;
  }

  // Filter out phase tools completion events
  if (
    'toolName' in step &&
    (step as ToolExecutionStep).toolName ===
      'comos.employees.publish-employee-phase-tools-completed-event'
  ) {
    return true;
  }

  // Filter out workflow execution containers
  if (isWorkflowContainer(step)) {
    return true;
  }

  return false;
}

/**
 * Parse parent execution stepId to extract phase and step number
 * Format: "{phase}-{stepNumber-1}-iteration-{iteration}"
 * Example: "planner-2-iteration-0" -> phase: "planner", stepNumber: 3, iteration: 0
 */
function parseParentExecutionStepId(
  stepId: string,
  executionId: string
): ParsedParentExecution | null {
  const match = stepId.match(/^([a-z-]+)-(\d+)-iteration-(\d+)$/);
  if (!match) {
    return null;
  }

  const [, phase, stepNumberMinusOne, iteration] = match;
  return {
    phase,
    stepNumber: parseInt(stepNumberMinusOne, 10) + 1,
    iteration: parseInt(iteration, 10),
    executionId,
  };
}

/**
 * Enrich a tool step with data from parent executor's executedTools
 */
function enrichToolStepFromExecutor(
  tool: ToolExecutionStep,
  parentPhase: TimelineStep | null
): ToolExecutionStep {
  if (!parentPhase || parentPhase.type !== 'employee-phase') {
    return tool;
  }

  const phaseData = parentPhase.data as EmployeePhaseStep;

  // Only enrich from executor phases
  if (phaseData.phase !== 'executor') {
    return tool;
  }

  const executorOutputs =
    phaseData.outputs && typeof phaseData.outputs === 'object'
      ? (phaseData.outputs as Record<string, unknown>)
      : {};
  const executedTools = Array.isArray(executorOutputs.executedTools)
    ? executorOutputs.executedTools
    : [];

  // Find matching executed tool by name and timestamp (±3s tolerance)
  const toolCreatedAt = tool.createdAt ? new Date(tool.createdAt).getTime() : 0;
  const matchedTool = executedTools.find((et: unknown) => {
    if (typeof et !== 'object' || et === null) return false;

    const executedTool = et as Record<string, unknown>;
    const nameMatches = executedTool.name === tool.toolName;

    if (!nameMatches) return false;

    // Try exact timestamp match first
    if (typeof executedTool.executedAt === 'string') {
      const executedAt = new Date(executedTool.executedAt).getTime();
      const timeDiff = Math.abs(toolCreatedAt - executedAt);
      // 3 second tolerance
      return timeDiff <= 3000;
    }

    return false;
  });

  if (!matchedTool || typeof matchedTool !== 'object') {
    return tool;
  }

  const executed = matchedTool as Record<string, unknown>;

  // Create enriched outputs
  const enrichedOutputs = { ...(tool.outputs ?? {}) };

  // Copy alias if present
  if (executed.alias && typeof executed.alias === 'string') {
    enrichedOutputs.alias = executed.alias;
  }

  // Copy parameters if present
  if (executed.parameters && typeof executed.parameters === 'object') {
    enrichedOutputs.parameters = executed.parameters;
  }

  // Copy result data if present
  if (executed.result && typeof executed.result === 'object') {
    const result = executed.result as Record<string, unknown>;

    if (result.responseData !== undefined) {
      enrichedOutputs.responseData = result.responseData;
    }

    if (typeof result.statusCode === 'number') {
      enrichedOutputs.statusCode = result.statusCode;
    }

    if (result.responseHeaders && typeof result.responseHeaders === 'object') {
      enrichedOutputs.responseHeaders = result.responseHeaders;
    }
  }

  return {
    ...tool,
    outputs: enrichedOutputs,
  };
}

/**
 * Find parent employee phase for a tool execution
 * Since intermediate tool iterations don't create employee phases,
 * we need to find the next available phase of the same type
 */
function findParentPhase(
  tool: ToolExecutionStep,
  phases: Map<string, TimelineStep>,
  allPhases: EmployeePhaseStep[]
): TimelineStep | null {
  if (!tool.parentExecution) {
    return null;
  }

  // Parse execution ID from parent format: "employee_execution-{executionId}"
  const executionIdMatch = tool.parentExecution.id.match(
    /^employee_execution-(.+)$/
  );
  if (!executionIdMatch) {
    return null;
  }

  const executionId = executionIdMatch[1];

  // Parse the stepId to get phase and step number reference
  const parsed = parseParentExecutionStepId(
    tool.parentExecution.stepId,
    executionId
  );
  if (!parsed) {
    return null;
  }

  // Find the phase with same executionId and phase name that has
  // stepNumber >= calculated stepNumber (closest match)
  const matchingPhases = allPhases
    .filter(
      (phase) =>
        phase.executionId === parsed.executionId &&
        phase.phase === parsed.phase &&
        phase.stepNumber >= parsed.stepNumber
    )
    .sort((a, b) => a.stepNumber - b.stepNumber);

  if (matchingPhases.length === 0) {
    return null;
  }

  // Get the closest matching phase (smallest stepNumber >= calculated)
  const targetPhase = matchingPhases[0];
  const lookupKey = `${targetPhase.executionId}:${targetPhase.phase}:${targetPhase.stepNumber}`;
  return phases.get(lookupKey) || null;
}

/**
 * Get creation timestamp for sorting, with fallback to epoch
 */
function getCreatedAtTimestamp(step: RawExecutionStep): number {
  return step.createdAt ? new Date(step.createdAt).getTime() : 0;
}

/**
 * Sort timeline steps by creation date
 */
function sortByCreatedAt(steps: TimelineStep[]): void {
  steps.sort((a, b) => {
    const timeA = getCreatedAtTimestamp(a.data);
    const timeB = getCreatedAtTimestamp(b.data);
    return timeA - timeB;
  });
}

/**
 * Main function to organize raw execution steps into hierarchical timeline
 *
 * @param rawSteps - Array of raw execution steps from the API
 * @returns Organized timeline with employee phases at top level and tools nested
 */
export function organizeTimelineSteps(
  rawSteps: RawExecutionStep[]
): TimelineStep[] {
  // Filter out steps that shouldn't appear in timeline
  const filteredSteps = rawSteps.filter((step) => !shouldFilterStep(step));

  // Collect all employee phases for matching
  const allPhases = filteredSteps.filter(isEmployeePhase);

  // Maps for organizing steps
  const phaseMap = new Map<string, TimelineStep>();
  const orphanedTools: TimelineStep[] = [];
  const topLevelSteps: TimelineStep[] = [];

  // First pass: Identify and organize employee phases
  // NOTE: We only add employee phases at top level. Tools will be nested in second pass.
  // We intentionally skip standalone workflows to keep timeline focused on employee execution flow.
  allPhases.forEach((step) => {
    const timelineStep: TimelineStep = {
      type: 'employee-phase',
      data: step,
      children: [],
    };

    // Create lookup key: "{executionId}:{phase}:{stepNumber}"
    const key = `${step.executionId}:${step.phase}:${step.stepNumber}`;
    phaseMap.set(key, timelineStep);
    topLevelSteps.push(timelineStep);
  });

  // Second pass: Attach tools to their parent phases and enrich with executor data
  filteredSteps.forEach((step) => {
    if (isToolExecution(step)) {
      const parentPhase = findParentPhase(step, phaseMap, allPhases);

      // Enrich tool step with data from parent executor's executedTools
      const enrichedStep = enrichToolStepFromExecutor(step, parentPhase);

      const timelineStep: TimelineStep = {
        type: 'tool',
        data: enrichedStep,
        children: [],
      };

      if (parentPhase) {
        parentPhase.children.push(timelineStep);
      } else {
        // Orphaned tool - add to top level with warning
        console.warn('Orphaned tool found - could not match to parent phase:', {
          toolName: step.toolName,
          executionId: step.executionId,
          workflowId: step.workflowId,
          parentExecution: step.parentExecution,
        });
        orphanedTools.push(timelineStep);
      }
    }
  });

  // Sort children within each phase
  topLevelSteps.forEach((step) => {
    if (step.children.length > 0) {
      sortByCreatedAt(step.children);
    }
  });

  // Combine top-level steps with orphaned tools
  const allTopLevel = [...topLevelSteps, ...orphanedTools];

  // Sort top-level steps by creation date
  sortByCreatedAt(allTopLevel);

  return allTopLevel;
}
